import React, { useState } from "react";
import { Button } from "primereact/button";
import type { KpiDashboardData, SelectedPeriod } from "../../../types/kpiDashboard";
import { getKpiDataFromStorage } from "../KpiStorage";

import KpiStatCard from "./KpiStatCard";
import AnomalyAndInvoiceCharts from "./AnomalyAndInvoiceCharts";
import AlertSummaryCards from "./AlertSummaryCards";
import OverdueInvoicesTable from "./OverdueInvoicesTable";
import PeriodSelector from "./PeriodSelector";
import StationsCard from "./StationsCard";
import SyncKpiDataDialog from "./SyncKpiDataDialog";

interface Props {
  data: KpiDashboardData;
  selectedPeriod: SelectedPeriod;
  availablePeriods: SelectedPeriod[];
  onPeriodChange: (period: SelectedPeriod) => void;
  onEnergyClick: () => void;
  onStationsClick: () => void;
  onOnlineRateClick: () => void;
  onSessionsClick: () => void;
}

const WeeklyDashboard: React.FC<Props> = ({
  data,
  selectedPeriod,
  availablePeriods,
  onPeriodChange,
  onEnergyClick,
  onStationsClick,
  onOnlineRateClick,
  onSessionsClick,
}) => {
  const [uploadDialogVisible, setUploadDialogVisible] = useState(false);
  const isUsingUploadedData = getKpiDataFromStorage() !== null;
  // Safe to destructure directly — `data` is always initialised with `initialData` in useKpiDashboard,
  // so all top-level properties are guaranteed to exist. `currentWeek` may be null (e.g. before first
  // fetch), which is why optional chaining (?.) is used when accessing its nested fields below.
  const { currentWeek, anomalyTrend, invoiceTrend,
          overdueInvoices, failures, marketing, lastUpdated } = data;

  const currentAnomaly = anomalyTrend[anomalyTrend.length - 1] ?? null;
  const prevAnomaly    = anomalyTrend[anomalyTrend.length - 2] ?? null;

  const onlineV  = currentWeek?.onlinePct ?? 0;
  const eGrowthV = currentWeek?.energyGrowthPct ?? 0;
  const sGrowthV = currentWeek?.sessionGrowthPct ?? 0;

  const totalFailureHours = failures.reduce((sum, f) => sum + f.hours, 0);
  const failureSeverity   = totalFailureHours === 0 ? "success"
                          : totalFailureHours <= 10 ? "warning" : "danger";

  const onlineSeverity   = onlineV  >= 78 ? "success" : onlineV  >= 74 ? "warning" : "danger";
  const energySeverity   = eGrowthV >= 5  ? "success" : eGrowthV >= -5 ? "warning" : "danger";
  const sessionSeverity  = sGrowthV >= 5  ? "success" : sGrowthV >= -5 ? "warning" : "danger";

  return (
    <>
      <SyncKpiDataDialog
        visible={uploadDialogVisible}
        onHide={() => setUploadDialogVisible(false)}
      />

      {/* ── Header ── */}
      <div className="kpi-header flex justify-content-between align-items-center mb-4">
        <div className="flex align-items-center gap-3">
          <PeriodSelector
            periods={availablePeriods}
            value={selectedPeriod}
            onChange={onPeriodChange}
          />
          <p className="text-xs text-color-secondary m-0">
            Updated: {lastUpdated}
          </p>
          {isUsingUploadedData && (
            <span className="text-xs font-semibold" style={{ color: "var(--green-600)" }}>
              <i className="pi pi-check-circle mr-1" />
              Uploaded data active
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <Button
            label="Sync from Sheets"
            icon="pi pi-sync"
            size="small"
            outlined
            onClick={() => setUploadDialogVisible(true)}
          />
        </div>
      </div>

      {/* ── KPI Stat Cards ── */}
      <p className="kpi-section-label">Station Operations</p>
      <div className="grid">
        <div className="col-12 md:col-6 lg:col-2">
          <KpiStatCard
            title="Online Rate"
            value={`${onlineV.toFixed(1)}%`}
            subtitle={`Active: ${currentWeek?.active} · Inactive: ${currentWeek?.inactive}`}
            severity={onlineSeverity}
            icon="pi pi-wifi"
            onClick={onOnlineRateClick}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-2">
          <StationsCard currentWeek={currentWeek} onClick={onStationsClick} />
        </div>
        <div className="col-12 md:col-6 lg:col-2">
          <KpiStatCard
            title="Total Energy (kWh)"
            value={(currentWeek?.energyTotal ?? 0).toLocaleString()}
            subtitle={`AC: ${currentWeek?.energyAc} · DC: ${currentWeek?.energyDc}`}
            growth={eGrowthV}
            severity={energySeverity}
            icon="pi pi-bolt"
            onClick={onEnergyClick}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-2">
          <KpiStatCard
            title="Charging Sessions"
            value={(currentWeek?.sessionsTotal ?? 0).toLocaleString()}
            subtitle={`Daily avg: ${currentWeek?.dailySessions}`}
            growth={sGrowthV}
            severity={sessionSeverity}
            icon="pi pi-car"
            onClick={onSessionsClick}
          />
        </div>
        <div className="col-12 md:col-6 lg:col-2">
          <KpiStatCard
            title="Failure Duration"
            value={failures.length ? `${totalFailureHours}h` : "—"}
            subtitle={failures.length ? `${failures.length} incident${failures.length > 1 ? "s" : ""} · ${failures[0].station}` : "No failures recorded"}
            severity={failureSeverity}
            icon="pi pi-exclamation-triangle"
          />
        </div>
        <div className="col-12 md:col-6 lg:col-2">
          <KpiStatCard
            title="Ad Spend (Month)"
            value={`€${marketing.adsCostThisMonth.toLocaleString("de-DE", { minimumFractionDigits: 0 })}`}
            subtitle={`Prev: €${marketing.adsCostPrevMonth.toLocaleString("de-DE", { minimumFractionDigits: 0 })}`}
            severity="info"
            icon="pi pi-megaphone"
          />
        </div>
      </div>

      {/* ── Alert Summary ── */}
      <p className="kpi-section-label mt-4">Alerts & Anomalies</p>
      <AlertSummaryCards
        currentAnomaly={currentAnomaly}
        prevAnomaly={prevAnomaly}
        overdueInvoices={overdueInvoices}
        failures={failures}
      />

      {/* ── Anomaly & Invoice Charts ── */}
      <p className="kpi-section-label mt-4">Anomalies & Invoices</p>
      <AnomalyAndInvoiceCharts
        anomalyTrend={anomalyTrend}
        invoiceTrend={invoiceTrend}
      />

      {/* ── Overdue Invoices Table ── */}
      <p className="kpi-section-label mt-4">Overdue Invoice Detail</p>
      <div className="grid">
        <div className="col-12">
          <OverdueInvoicesTable invoices={overdueInvoices} />
        </div>
      </div>
    </>
  );
};

export default WeeklyDashboard;
